import * as d3 from "d3";
import { differenceInMinutes, differenceInSeconds, subSeconds } from "date-fns";

import { TimeFrameType } from "src/organization/pages/project/environment/metrics/resources/types";
import { parseTimeDuration } from "src/organization/pages/project/environment/metrics/utils/parseDimensions/parseDimensions";

export const margin = {
  top: 7,
  right: 32,
  bottom: 30,
  left: 32,
  height: 230
};

export const timeFormats = {
  tooltip: d3.timeFormat("%H:%M:%S"),
  tooltipLong: d3.timeFormat("%H:%M %b %d %Y"),
  seconds: d3.timeFormat("%H:%M"),
  minutes: d3.timeFormat("%H:%M"),
  hours: d3.timeFormat("%H:%M"),
  days: d3.timeFormat("%b %d"),
  weeks: d3.timeFormat("%b %d"),
  months: d3.timeFormat("%b %d")
};

const mapRange = (from: string | Date, to: string | Date) => {
  const start = new Date(from);
  const end = new Date(to);
  return { start, from: start, to: end, end };
};

const durationStringToRange = (durationText: string) => {
  const duration = parseTimeDuration(durationText);
  const to = new Date();
  const from = subSeconds(to, duration.seconds);
  return mapRange(from, to);
};

// Resolves a TimeFrameType to a concrete { start, end } date range.
// Returns undefined when the timeframe has no usable fields.
// The API client wraps this with requireTimeframeRange() which throws.
export const timeframeToRange = (timeframe?: TimeFrameType) => {
  const { from, to, start, end, id } = timeframe || {};

  if (from && to) return mapRange(from, to);
  if (start && end) return mapRange(start, end);
  if (id && typeof id === "string" && id.length > 1)
    return durationStringToRange(id);

  return undefined;
};

const hashTimeRange = ({ start, end, from, to }: TimeFrameType) =>
  `${new Date(start || from || 0).getTime()}-${new Date(end || to || 0).getTime()}`;

export const getQueryInterval = (
  timeFrame: TimeFrameType,
  maxPoints: number | null = null
) => {
  const FALLBACK_INTERVAL = "60s"; // 1-minute granularity, matches the smallest interval bucket
  const range = timeframeToRange(timeFrame);
  if (!range) return FALLBACK_INTERVAL;
  const { start, end } = range;
  const rangeMinute = differenceInMinutes(end, start);

  const intervals = [
    { minutes: 15, unit: "1m" },
    { minutes: 60, unit: "1m" },
    { minutes: 480, unit: "5m" },
    { minutes: 1440, unit: "10m" },
    { minutes: 2880, unit: "30m" },
    { minutes: 10080, unit: "1h" },
    { minutes: 21600, unit: "2h" },
    { minutes: 43200, unit: "4h" },
    { minutes: 86400, unit: "8h" },
    { minutes: 129600, unit: "12h" },
    { minutes: 259200, unit: "24h" },
    { minutes: 525600, unit: "48h" }
  ];

  const getIntervalSeconds = (unit: string) =>
    `${parseTimeDuration(unit).seconds}s`;

  if (maxPoints) {
    const suitable = intervals.find(
      (intvl, idx) =>
        rangeMinute / intvl.minutes < maxPoints || idx === intervals.length - 1
    );
    return getIntervalSeconds(suitable?.unit || "48h");
  }

  const suitable = intervals.find(intvl => rangeMinute <= intvl.minutes);
  return getIntervalSeconds((suitable ?? intervals[intervals.length - 1]).unit);
};

/**
 * Compares two timeframes to determine if they represent the same time period.
 * For presets, compares id + generation directly (avoids resolving to Date.now()-
 * based ranges which would make two "24h" objects appear different 1 second apart).
 * For custom ranges, compares the resolved start/end timestamps.
 */
export const isEqualTimeframe = (t1: TimeFrameType, t2: TimeFrameType) => {
  if (!t1 || !t2) return false;
  if (t1.id && t2.id) {
    return t1.id === t2.id && (t1.generation ?? 0) === (t2.generation ?? 0);
  }
  const range1 = timeframeToRange(t1);
  const range2 = timeframeToRange(t2);
  if (!range1 || !range2) return false;
  return hashTimeRange(range1) === hashTimeRange(range2);
};

// Returns a new object with hash and poll fields set.
// Does not mutate the input — callers can pass shared objects safely.
// For presets, the hash is deterministic (derived from id + generation),
// so multiple calls with the same preset produce the same hash.
// User-initiated changes set generation to make the hash unique.
export const hashTimeFrame = (t: TimeFrameType): TimeFrameType => {
  if (t.hash) return t;
  const hash = t.id
    ? t.generation
      ? `${t.id}:${t.generation}`
      : t.id
    : hashTimeRange(t);
  return {
    ...t,
    hash,
    poll: t.id ? getQueryInterval(t) : null
  };
};

type TimeInterval =
  "seconds" | "minutes" | "hours" | "days" | "weeks" | "months";

export const getRangeFormat = ({ start, end }) => {
  const rangeSeconds = differenceInSeconds(new Date(end), new Date(start));
  const intervals: Record<TimeInterval, number> = {
    seconds: 3 * 60,
    minutes: 8 * 60 * 60,
    hours: 2 * 24 * 60 * 60,
    days: 7 * 24 * 60 * 60,
    weeks: 4 * 7 * 24 * 60 * 60,
    months: 2 * 30 * 24 * 60 * 60
  };
  for (const [interval, threshold] of Object.entries(intervals).reverse()) {
    if (rangeSeconds >= threshold) {
      return interval;
    }
  }
  return "minutes";
};
